import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import { IconButton, Tooltip } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { IFileViewModel } from 'viewModels';
import { IRefRegisterTypeProfile, IRegisterTypeProfile } from 'entities';

import { useAuth, useRegisterForm } from '../../context';
import {
  FileUsageType,
  labelUserProfileType,
  labelValidationType,
  UserProfileType,
  ValidationType,
} from '../../types/enums';
import { DropZone } from '../DropZone';
import { Container, Content, ContentSummary } from './styles';
import { Input } from '../Inputs/Input';
import { DropDownList } from '../DropDownList';
import { BRAZILIAN_STATES, UF_BY_CODE, UF_CODE_BY_NAME } from '../../constants';
import { ProfileVerified } from '../Verified';

export const RegisterTypeProfile = forwardRef<IRefRegisterTypeProfile>((_, ref) => {
  const refComponent = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const color =
    location.pathname === '/naturalPersonAccount' || location.pathname === '/legalPersonAccount'
      ? 'background'
      : 'background_white';
  const [typeValidationField, setTypeValidationField] = useState<ValidationType>();
  const [verificationPicturesField, setVerificationPicturesField] = useState<
    (IFileViewModel | undefined)[]
  >([undefined, undefined]);
  const [defaultValues] = useState({ inscriptionNumber: '', uf: '' });
  const [displaySummary, setDisplaySummary] = useState(false);
  const { user } = useAuth();
  const {
    activeForm,
    verificationPictures,
    typeProfile,
    setTypeProfile,
    typeValidation,
    inscriptionNumber,
    isVerified,
    setActiveForm,
    setIsVerified,
  } = useRegisterForm();

  const {
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const data = watch();

  const handleHiddenSummary = () => {
    setDisplaySummary(!displaySummary);
    setActiveForm('registerTypeProfile');
  };

  useEffect(() => {
    if (activeForm === 'registerTypeProfile') {
      setDisplaySummary(true);
    } else {
      setDisplaySummary(false);
    }
  }, [activeForm]);

  useEffect(() => {
    setVerificationPicturesField([undefined, undefined]);
  }, [typeValidation]);

  useEffect(() => {
    setValue('inscriptionNumber', inscriptionNumber);
    if (user.ufInscriptionNumber)
      setValue('uf', UF_CODE_BY_NAME[user.ufInscriptionNumber]?.toString());
    setTypeProfile(typeProfile);
    setTypeValidationField(typeValidation);
    setVerificationPicturesField(verificationPictures);
  }, [
    inscriptionNumber,
    isVerified,
    setTypeProfile,
    setValue,
    typeProfile,
    typeValidation,
    user.ufInscriptionNumber,
    verificationPictures,
  ]);

  const handleFocusOnError = () => {
    refComponent.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleUpdateTypeProfile = () => {
    const hasError = false;
    const newData: IRegisterTypeProfile = {
      typeProfile,
      typeValidation: typeValidationField,
      verificationPictures: verificationPicturesField,
      inscriptionNumber: data.inscriptionNumber,
      ufInscriptionNumber: UF_BY_CODE[Number(data.uf)],
      isVerified,
      hasError,
    };

    if (typeProfile === UserProfileType.LAWYER) {
      if (!data.inscriptionNumber) {
        setError('inscriptionNumber', {
          type: 'manual',
          message: 'Campo obrigatório',
        });
        newData.hasError = true;
      }
      if (!data.uf) {
        setError('uf', {
          type: 'manual',
          message: 'Campo estado é obrigatório',
        });
        newData.hasError = true;
      }
    }

    return newData;
  };

  const handleTourDefaultValues = () => {
    setTypeProfile(UserProfileType.OTHERS);
    setDisplaySummary(true);
    setActiveForm('registerTypeProfile');
  };
  useImperativeHandle(ref, () => ({
    handleTourDefaultValues,
    handleUpdateTypeProfile,
    handleFocusOnError,
  }));

  useEffect(() => {
    if (data.inscriptionNumber) clearErrors('inscriptionNumber');
    if (data.uf) clearErrors('uf');
  }, [clearErrors, data.inscriptionNumber, data.uf]);
  useEffect(() => {
    setValue(
      'uf',
      user.ufInscriptionNumber ? UF_CODE_BY_NAME[user.ufInscriptionNumber]?.toString() : '',
    );
    setValue('inscriptionNumber', inscriptionNumber);
  }, [inscriptionNumber, setValue, user.ufInscriptionNumber]);
  return (
    <Container color={color} ref={refComponent}>
      {displaySummary && (
        <Content>
          <div>
            <div className="content">
              <div className="section">
                <div className="header">
                  <p className="title">Tipo de perfil</p>
                </div>
                <div id="profile-type-dropdown">
                  <DropDownList
                    value={typeProfile}
                    itens={[
                      {
                        id: UserProfileType.LAWYER,
                        value: labelUserProfileType[UserProfileType.LAWYER],
                      },
                      {
                        id: UserProfileType.TRAINEE,
                        value: labelUserProfileType[UserProfileType.TRAINEE],
                      },
                      {
                        id: UserProfileType.OTHERS,
                        value: labelUserProfileType[UserProfileType.OTHERS],
                      },
                    ]}
                    onChange={(value) => {
                      setTypeProfile(value as unknown as UserProfileType);
                      setIsVerified(false);
                      setValue('inscriptionNumber', '');
                      setValue('uf', '');
                    }}
                    typeReturn="id"
                  />
                </div>
              </div>
              {typeProfile === UserProfileType.LAWYER && (
                <div className="section">
                  <div className="containerNumberInscription" id="inscription-number">
                    <Input
                      placeholder="Número de inscrição"
                      mask="999999999999999"
                      lengthInput={15}
                      className="TextField"
                      variant="standard"
                      name="inscriptionNumber"
                      onChange={() => {
                        setIsVerified(false);
                      }}
                      control={control}
                      error={!!errors.inscriptionNumber}
                      errorText={errors.inscriptionNumber?.message}
                    />
                    <Controller
                      control={control}
                      name="uf"
                      render={({ field: { onChange } }) => (
                        <DropDownList
                          placeholderTitle="Estado"
                          defaultValue={data.uf ?? ''}
                          itens={
                            BRAZILIAN_STATES.map((state) => ({
                              id: state.ufCode,
                              value: state.name,
                            })) ?? []
                          }
                          onChange={(e) => {
                            onChange(e);
                            setIsVerified(false);
                          }}
                          typeReturn="id"
                          errorText={errors.uf?.message}
                        />
                      )}
                    />
                    {/* <Tooltip disableInteractive  title="Validar número de inscrição" arrow>
                      <IconButton
                        onClick={handleValidationInscriptionNumber}
                        disabled={!data.inscriptionNumber && !data.uf}>
                        <Search />
                      </IconButton>
                    </Tooltip> */}
                  </div>
                </div>
              )}
              {typeProfile !== undefined && (
                <div className="section" id="validation-type">
                  <div className="header">
                    <p className="title">Validação de perfil</p>
                  </div>
                  <DropDownList
                    placeholderTitle="Selecione"
                    itens={[
                      { id: ValidationType.OAB, value: labelValidationType[ValidationType.OAB] },
                      { id: ValidationType.RG, value: labelValidationType[ValidationType.RG] },
                      { id: ValidationType.CPF, value: labelValidationType[ValidationType.CPF] },
                    ]}
                    onChange={(value) => setTypeValidationField(value as unknown as ValidationType)}
                    typeReturn="id"
                    defaultValue={typeValidationField?.toString()}
                  />
                </div>
              )}
            </div>
            <div>
              <div
                className={typeValidationField !== undefined ? 'containerDropzone' : 'displayNone'}>
                <div className="contentDropzone">
                  <h1>Frente</h1>
                  <div className="dropzone">
                    <DropZone
                      multipleFiles={false}
                      type="picture"
                      onFile={(file) => {
                        setVerificationPicturesField((prev) => {
                          const newVerificationPictures = [...prev];
                          const newVerificationPicture = {
                            ...file,
                            fileUsageType: FileUsageType.FRONT,
                          };
                          newVerificationPictures[0] = newVerificationPicture;
                          return newVerificationPictures;
                        });
                      }}
                      onRemoveFile={() => {
                        setVerificationPicturesField((prev) => [undefined, prev?.[1]]);
                      }}
                      renderSummaryType="document"
                      fileId={
                        verificationPicturesField.find(
                          (item) => item?.fileUsageType === FileUsageType.FRONT,
                        )?.id
                      }
                      defaultValues={
                        verificationPicturesField.find(
                          (item) => item?.fileUsageType === FileUsageType.FRONT,
                        )?.file
                      }
                      renderSummary
                    />
                  </div>
                </div>
                <div className="contentDropzone">
                  <h1>Verso</h1>
                  <div className="dropzone">
                    <DropZone
                      multipleFiles={false}
                      type="picture"
                      onFile={(file) => {
                        setVerificationPicturesField((prev) => {
                          const newVerificationPictures = [...prev];
                          const newVerificationPicture = {
                            ...file,
                            fileUsageType: FileUsageType.BACK,
                          };
                          newVerificationPictures[1] = newVerificationPicture;
                          return newVerificationPictures;
                        });
                      }}
                      onRemoveFile={() => {
                        setVerificationPicturesField((prev) => [prev?.[0], undefined]);
                      }}
                      renderSummaryType="document"
                      fileId={
                        verificationPicturesField.find(
                          (item) => item?.fileUsageType === FileUsageType.BACK,
                        )?.id
                      }
                      defaultValues={
                        verificationPicturesField.find(
                          (item) => item?.fileUsageType === FileUsageType.BACK,
                        )?.file
                      }
                      renderSummary
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="containerProfileVerified">
              {typeProfile === UserProfileType.LAWYER ? (
                isVerified && (
                  <Tooltip disableInteractive title="Número de inscrição regular no CNA/OAB">
                    <h1>
                      Perfil verificado
                      <ProfileVerified color="tertiary_500" />
                    </h1>
                  </Tooltip>
                )
              ) : (
                <h1>
                  Perfil verificado
                  <ProfileVerified color="yellow_200" />
                </h1>
              )}
            </div>
            <div className="warning">
              {/* {typeProfile === UserProfileType.LAWYER && !isVerified && (
                <h1>
                  Para prestar serviços, advogados precisam que o número de inscrição esteja
                  regular*
                </h1>
              )} */}
            </div>
          </div>
          <IconButton onClick={handleHiddenSummary}>
            <ExpandCircleDownIcon
              fontSize="large"
              color={
                typeProfile !== undefined || isVerified || !!data.inscriptionNumber
                  ? 'success'
                  : 'error'
              }
              className="icon"
            />
          </IconButton>
        </Content>
      )}
      {!displaySummary && (
        <ContentSummary>
          <div>
            <div className="headerSummary">
              <button
                type="button"
                className="title"
                onClick={handleHiddenSummary}
                id="expand-profile-type">
                Tipo de perfil
              </button>
              <IconButton onClick={handleHiddenSummary}>
                <ExpandCircleDownIcon
                  fontSize="large"
                  color={
                    typeProfile !== undefined || isVerified || !!data.inscriptionNumber
                      ? 'success'
                      : 'error'
                  }
                  className="icon"
                />
              </IconButton>
            </div>
            <div className={typeProfile !== undefined ? 'contentType' : 'displayNone'}>
              <button type="button" className="text" onClick={handleHiddenSummary}>
                {labelUserProfileType[typeProfile as UserProfileType]}
              </button>
            </div>
          </div>
          {typeProfile === UserProfileType.LAWYER && (
            <div>
              <div className="headerSummary">
                <button type="button" className="title" onClick={handleHiddenSummary}>
                  Número de Inscrição
                </button>
              </div>
              <div className={data.inscriptionNumber !== '' ? 'contentType' : 'displayNone'}>
                <button type="button" className="text" onClick={handleHiddenSummary}>
                  {data.inscriptionNumber}
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="headerSummary">
              <button type="button" className="title" onClick={handleHiddenSummary}>
                Validação de perfil
              </button>
            </div>
          </div>
          <div className={isVerified ? 'contentType' : 'displayNone'}>
            {typeProfile === UserProfileType.LAWYER && isVerified && (
              <button type="button" className="text" onClick={handleHiddenSummary}>
                Perfil verificado
                <ProfileVerified color="tertiary_500" />
              </button>
            )}
          </div>
        </ContentSummary>
      )}
    </Container>
  );
});
